import {
  Box,
  Divider,
  Flex,
  FlexProps,
  FormControl,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  LinkBox,
  LinkOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import Logo from '../../assets/images/9jaPay Logo_Horizontal.svg';
import { SecondaryButton } from '../../components/custom';
import {
  BarsIcon,
  BellIcon,
  BellSlashIcon,
  BlackCheckCircleIcon,
  BlackChevronDownIcon,
  BlackMiniBriefcaseIcon,
  BlackPlusAddIcon,
  ChevronDownIcon,
  OutletBriefcaseFilledIcon,
  OutletBriefcaseIcon,
  ProfileIcon,
  SearchIcon,
  SettingsIcon,
} from '../../components/icons';
import { getLastPathTitle, getTitleCase } from '../../helpers/utilityFunctions';
import useBusinessRegistrationType from '../../hooks/useBusinessRegistrationType';
import useGetBusinessDetails from '../../hooks/useGetBusinessDetails';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getAccountBalanceAsync } from '../../store/slices/account/getAccountBalanceSlice';
import { getAccountsAsync } from '../../store/slices/account/getAccountsSlice';
import { devAccessAsync } from '../../store/slices/auth/devAuthSlice';
import { signOut } from '../../store/slices/auth/signOutSlice';
import { getUserBusinessesAsync } from '../../store/slices/business/getUserBusinessesSlice';
import { getUserDetailsForBusinessAsync } from '../../store/slices/business/getUserDetailsForBusinessSlice';
import {
  getOutletsAsync,
  IGetOutletsResponse,
} from '../../store/slices/outlet/getOutlets';
import { DRAWER_WIDTH } from './AppSidebar';

interface AppHeaderProps extends FlexProps {
  onOpen: () => void;
  top?: number;
}

interface IOutlet {
  outletName: string;
  accountNumber: string;
  accountName: string;
  commissionsAccountNumber: string;
}

/** Titles for pages whose display name differs from their path. */
const pageTitleOverrides: Record<string, string> = {
  '/app/all-account': 'Account Summary',
};

const AppHeader = ({ onOpen, top = 0, ...rest }: AppHeaderProps) => {
  const { pathname } = useLocation();
  const isSavingsBucketDetailsPage = /^\/app\/savings\/buckets\/[^/]+$/.test(
    pathname,
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchKey, setSearchKey] = useState<string>('');

  const url = process.env.REACT_APP_API_DOCS_URL;

  const { isBNBusiness } = useBusinessRegistrationType();

  const { value: authValue } = useAppSelector((state) => state.auth);

  const handleSignOut = () => {
    dispatch(signOut());
    navigate('./');
  };

  const handleGoToSettings = () => {
    navigate('./settings?view=profile');
  };

  const { status: businessesStatus, value: businessesValue } = useAppSelector(
    (state) => state.userBusinesses,
  );

  const { status: outletsStatus, value: outletsValue } = useAppSelector(
    (state) => state.getOutlets,
  );

  const { status: userDetailsStatus, value: userDetailsValue } = useAppSelector(
    (state) => state.userDetailsForBusinessFetch,
  );

  const outletFromStorage = localStorage.getItem('currentOutlet');
  const parsedOutletFromStorage = outletFromStorage
    ? JSON.parse(outletFromStorage)
    : null;

  const userHasAccountFromStorage = localStorage.getItem('userHasAccount');

  const businessId = localStorage.getItem('businessId');

  const [activeOutlet, setActiveOutlet] = useState<
    Omit<IOutlet, 'accountName'>
  >(
    parsedOutletFromStorage ?? {
      outletName: 'Accounts',
      accountNumber: '',
    },
  );

  const [filteredOutlets, setFilteredOutlets] = useState<
    IGetOutletsResponse['data']
  >([]);

  const userHasAccount = authValue?.data?.user?.businesses?.find(
    (business) => business?.id === businessId,
  )?.hasAccount;

  useEffect(() => {
    if (userHasAccount && userHasAccountFromStorage === null) {
      localStorage.setItem('userHasAccount', 'true');
    } else if (!userHasAccount && userHasAccountFromStorage) {
      localStorage.removeItem('userHasAccount');
    }
  }, [userHasAccount, userHasAccountFromStorage]);

  useEffect(() => {
    if (businessId) {
      dispatch(getAccountsAsync());
      dispatch(getOutletsAsync());
      dispatch(getUserDetailsForBusinessAsync());
      dispatch(getAccountBalanceAsync());
    }
  }, [dispatch, businessId]);

  useEffect(() => {
    if (userHasAccount && userHasAccountFromStorage === null) {
      localStorage.setItem('userHasAccount', 'true');
    } else if (!userHasAccount && userHasAccountFromStorage) {
      localStorage.removeItem('userHasAccount');
    }
  }, [userHasAccount, userHasAccountFromStorage]);

  useEffect(() => {
    const developerIdInStorage = localStorage.getItem('developerId');
    const devTokenInStorage = localStorage.getItem('devTokenData');

    if (developerIdInStorage && !devTokenInStorage) {
      dispatch(devAccessAsync());
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    if (!activeOutlet.accountNumber && outletsStatus === 'success') {
      setActiveOutlet({
        outletName: getTitleCase(filteredOutlets?.[0]?.name ?? 'Accounts'),
        accountNumber: filteredOutlets?.[0]?.account?.accountNumber ?? '',
        commissionsAccountNumber:
          filteredOutlets?.[0]?.account?.commissionsAccountNumber ?? '',
      });
    }
  }, [activeOutlet.accountNumber, filteredOutlets, outletsStatus]);

  const isActive = (accountNumber?: string) =>
    accountNumber === activeOutlet.accountNumber;

  const businessDetails = useGetBusinessDetails();

  const isBusinessOwner =
    businessDetails?.creatorType?.toLowerCase() === 'owner';

  useEffect(() => {
    if (outletsStatus === 'success') {
      if (isBusinessOwner) {
        setFilteredOutlets(outletsValue?.data);
      } else if (userDetailsStatus === 'success') {
        if (userDetailsValue?.data?.outlets && outletsValue?.data) {
          const filtered = userDetailsValue?.data?.outlets.filter(
            (userOutlet) =>
              outletsValue?.data?.some(
                (outlet) => outlet?.id === userOutlet?.id,
              ),
          );
          setFilteredOutlets(filtered);
        } else {
          setFilteredOutlets([]);
        }
      }
    }
  }, [
    isBusinessOwner,
    outletsStatus,
    outletsValue?.data,
    userDetailsStatus,
    userDetailsValue?.data?.outlets,
  ]);

  const handleOutletChange = ({
    outletName,
    accountNumber,
    accountName,
    commissionsAccountNumber,
  }: IOutlet) => {
    const outlet = { outletName, accountNumber, commissionsAccountNumber };

    localStorage.removeItem('currentOutlet');
    localStorage.removeItem('availableBalance');

    setActiveOutlet(outlet);
    localStorage.setItem('currentOutlet', JSON.stringify(outlet));
    localStorage.setItem('accountNumber', accountNumber);
    localStorage.setItem('accountName', accountName);
    localStorage.setItem('commissionsAccountNumber', commissionsAccountNumber);

    window.location.reload();
  };

  const searchedOutlets = filteredOutlets?.filter((outlet) =>
    outlet?.name?.toLowerCase().includes(searchKey.toLowerCase()),
  );

  const handleSearchOutlets = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKey(event.target.value);
  };

  return (
    <Flex
      as="header"
      width={{ base: '100%', md: `calc(100% - ${DRAWER_WIDTH + 1}px)` }}
      px={{ base: 4, md: '44px' }}
      ml={{ base: 0, md: DRAWER_WIDTH + 1 }}
      height="20"
      alignItems="center"
      justifyContent="space-between"
      position="fixed"
      top={`${top}px`}
      zIndex="1001"
      background="#fafafa"
      // maxW="1728px"
      // margin="auto"
      {...rest}
    >
      <IconButton
        display={{ base: 'flex', md: 'none' }}
        onClick={onOpen}
        variant="ghost"
        aria-label="open menu"
        icon={<BarsIcon w="24px" />}
      />

      <LinkBox display={{ base: 'flex', md: 'none' }} justifyContent="center">
        <LinkOverlay as={RouterLink} to="/app">
          <Image src={Logo} w="60%" />
        </LinkOverlay>
      </LinkBox>

      <Text
        id="page-header-title"
        display={{ base: 'none', md: 'block' }}
        fontWeight={500}
      >
        {isSavingsBucketDetailsPage
          ? 'Bucket Details'
          : pageTitleOverrides[pathname] ?? getLastPathTitle(pathname)}
      </Text>

      <HStack spacing={{ base: '0', md: '1' }} alignItems="center">
        <Box
          display={{ base: 'none', md: 'flex' }}
          alignItems="center"
          gap={{ base: '0', md: '1' }}
        >
          {!isBNBusiness && (
            <Box borderRadius="40px" bg="#ffffff" padding="8px">
              <Link href={url} isExternal>
                <Text fontSize="14px" fontWeight={500} mr={2}>
                  API Documentation
                </Text>
              </Link>
            </Box>
          )}
          <Tooltip label="Settings" hasArrow>
            <IconButton
              as={RouterLink}
              to="/app/settings"
              size="lg"
              variant="ghost"
              _hover={{
                background: 'transparent',
              }}
              aria-label="settings"
              icon={<SettingsIcon w="18px" />}
            />
          </Tooltip>

          <Menu matchWidth>
            <MenuButton transition="all 0.3s" _focus={{ boxShadow: 'none' }}>
              <IconButton
                as={Box}
                size="lg"
                variant="ghost"
                _hover={{
                  background: 'transparent',
                }}
                aria-label="open notifications"
                icon={<BellIcon w="18px" />}
              />
            </MenuButton>
            <MenuList bg="white" borderColor="gray.100" w="200px">
              <MenuItem
                fontSize=".875rem"
                lineHeight="150%"
                noOfLines={1}
                borderRadius="4px"
                display="flex"
                height="200px"
                _focus={{ boxShadow: 'none' }}
              >
                <Box py="80px" maxW="307px" mx="auto">
                  <BellSlashIcon
                    boxSize="100px"
                    display="block"
                    margin="auto"
                    mb="6"
                  />
                  <Text textAlign="center" mb={1} fontWeight={500}>
                    There is nothing to see here yet
                  </Text>
                </Box>
              </MenuItem>
            </MenuList>
          </Menu>
        </Box>

        <Flex alignItems="center">
          <Menu matchWidth>
            <MenuButton
              py={2}
              px={2}
              transition="all 0.3s"
              _focus={{ boxShadow: 'none' }}
            >
              <HStack mr={2} justifyContent="space-between">
                <ProfileIcon />

                <Box display={{ base: 'none', md: 'flex' }}>
                  <ChevronDownIcon w="20px" />
                </Box>
              </HStack>
            </MenuButton>
            <MenuList bg="white" borderColor="gray.100" w="100px">
              <MenuItem
                fontSize=".875rem"
                lineHeight="150%"
                noOfLines={1}
                borderRadius="4px"
                onClick={handleGoToSettings}
                display="flex"
                justifyContent="space-between"
              >
                Profile
              </MenuItem>
              <MenuItem
                fontSize=".875rem"
                lineHeight="150%"
                borderRadius="4px"
                onClick={handleSignOut}
                textAlign="center"
                justifyContent="center"
              >
                Sign out
              </MenuItem>
            </MenuList>
          </Menu>

          {businessesStatus === 'failed' && (
            <SecondaryButton onClick={() => dispatch(getUserBusinessesAsync())}>
              Refetch Businesses
            </SecondaryButton>
          )}

          {outletsStatus === 'success' && !!businessesValue?.data?.length && (
            <Menu>
              <MenuButton
                py={2}
                px={2}
                transition="all 0.3s"
                _focus={{ boxShadow: 'none' }}
                borderRadius="44px"
                background="#00151A"
                color="#FFFFFF"
                padding="8px 6px 8px 12px"
                width={{ base: '150px', md: '192px' }}
              >
                <HStack mr={2} justifyContent="space-between">
                  <BlackMiniBriefcaseIcon />

                  <Text
                    maxW="104px"
                    fontSize=".875rem"
                    fontWeight={500}
                    noOfLines={1}
                    textAlign="left"
                  >
                    {/* {businessName} */}
                    {getTitleCase(activeOutlet?.outletName)}
                  </Text>

                  <Box display={{ base: 'none', md: 'flex' }}>
                    <BlackChevronDownIcon w="20px" />
                  </Box>
                </HStack>
              </MenuButton>
              <MenuList bg="white" borderColor="gray.100" maxW="320px">
                <HStack
                  justifyContent="space-between"
                  bg="#E6E8E8"
                  borderRadius="8px"
                  p="10px"
                >
                  <Box>
                    <Text fontWeight={500}>{activeOutlet?.outletName}</Text>
                    <Text fontSize="12px" color="#8A9396">
                      Outlet Number - {activeOutlet?.accountNumber}
                    </Text>
                  </Box>
                  <Box cursor="pointer">
                    <BlackPlusAddIcon />
                  </Box>
                </HStack>
                <Box mt="6px" mb="16px">
                  <FormControl id="search-outlet">
                    <InputGroup>
                      <InputLeftElement pointerEvents="none" pt="4" pb="2.5">
                        <SearchIcon />
                      </InputLeftElement>
                      <Input
                        variant="rounded"
                        placeholder="Search Outlet"
                        value={searchKey}
                        type="search"
                        onChange={handleSearchOutlets}
                        sx={{
                          backgroundColor: 'ash.100',
                          borderRadius: '24px',
                          minW: { base: '100%', md: '304px' },
                          height: '44px',
                          pl: '40px',
                        }}
                      />
                    </InputGroup>
                  </FormControl>
                </Box>
                <Text
                  textTransform="uppercase"
                  fontSize="10px"
                  fontWeight={500}
                  color="#546266"
                  ml="6px"
                >
                  Switch Outlet
                </Text>
                <Box maxHeight="90px" overflowY="auto" mb="16px">
                  {searchedOutlets?.map((outlet) => (
                    <Box key={outlet?.id} ml="6px">
                      <Tooltip
                        display={outlet.isActive ? 'none' : 'inherit'}
                        label="Outlet is disabled"
                        hasArrow
                      >
                        <>
                          <HStack>
                            {isActive(outlet.account?.accountNumber) ? (
                              <OutletBriefcaseFilledIcon />
                            ) : (
                              <OutletBriefcaseIcon />
                            )}
                            <Box
                              px="2"
                              py="1"
                              borderRadius="4px"
                              fontSize="14px"
                              transition="all 0.2s"
                              cursor={
                                outlet.isActive ? 'pointer' : 'not-allowed'
                              }
                              _hover={{ background: 'gray.400', color: '#fff' }}
                              onClick={() => {
                                if (outlet.isActive) {
                                  handleOutletChange({
                                    outletName: outlet?.name ?? '',
                                    accountNumber:
                                      outlet?.account?.accountNumber ?? '',
                                    accountName:
                                      outlet?.account?.accountName ?? '',
                                    commissionsAccountNumber:
                                      outlet?.account
                                        ?.commissionsAccountNumber ?? '',
                                  });
                                }
                              }}
                              mt="1px"
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              width="100%"
                            >
                              <Box
                                maxW="160px"
                                whiteSpace="nowrap"
                                overflow="hidden"
                                textOverflow="ellipsis"
                              >
                                {outlet?.name}
                              </Box>
                            </Box>
                            <BlackCheckCircleIcon
                              sx={{
                                display: isActive(outlet.account?.accountNumber)
                                  ? 'initial'
                                  : 'none',
                              }}
                            />
                          </HStack>
                        </>
                      </Tooltip>
                    </Box>
                  ))}
                  <Divider display={{ base: 'inherit', md: 'none' }} />

                  <MenuItem
                    as={RouterLink}
                    to="/app/settings"
                    fontSize=".875rem"
                    lineHeight="150%"
                    borderRadius="4px"
                    display={{ base: 'inherit', md: 'none' }}
                  >
                    Settings
                  </MenuItem>
                </Box>
              </MenuList>
            </Menu>
          )}
        </Flex>
      </HStack>
    </Flex>
  );
};

export default AppHeader;
